import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MovieDetailModal } from "./MovieDetailModal";
import { RatingDialog } from "./RatingDialog";
import { WatchStatusSelector, WatchStatus } from "./WatchStatusSelector";
import { Star, Clock, Calendar, Info, FolderPlus, Ticket, Clapperboard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { requestTicket } from "@/lib/guest";

export interface CollectionInfo {
  id: string;
  name: string;
}

interface GenreRating {
  genre: string;
  score: number;
  note: string;
}

interface EnhancedMovieCardProps {
  id: number;
  title: string;
  year: number | null;
  overview: string;
  poster: string | null;
  rating: number;
  genres?: string[];
  runtime?: number;
  media_type: 'movie' | 'tv';
  certification?: string;
  /** @deprecated kept for compatibility but unused — WatchStatusSelector handles status */
  isInWatchlist?: boolean;
  /** @deprecated kept for compatibility but unused — WatchStatusSelector handles status */
  onToggleWatchlist?: (id: number, mediaType: 'movie' | 'tv', title?: string, posterPath?: string, year?: number | null) => void;
  isPremium?: boolean;
  userId?: string;
  showTrailer?: boolean;
  collections?: CollectionInfo[];
  ai_match_score?: number;
  genre_rating?: GenreRating;
  pickReason?: string;
  /** When set, the badge shows this person's rating (hidden when unrated). */
  ratingLabel?: string;
  currentWatchStatus?: WatchStatus | null;
  onWatchStatusChange?: (newStatus: WatchStatus | null) => void;
}

export const EnhancedMovieCard = React.memo(({
  id,
  title,
  year,
  overview,
  poster,
  rating,
  genres = [],
  runtime,
  media_type,
  certification,
  isInWatchlist = false,
  onToggleWatchlist,
  isPremium = false,
  userId,
  showTrailer = false,
  collections: collectionsProp,
  ai_match_score,
  genre_rating,
  pickReason,
  ratingLabel,
  currentWatchStatus,
  onWatchStatusChange,
}: EnhancedMovieCardProps) => {
  const [showModal, setShowModal] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [localCollections, setLocalCollections] = useState<CollectionInfo[]>([]);
  const { toast } = useToast();

  // Use prop if provided, otherwise fall back to local fetch
  const collections = collectionsProp ?? localCollections;

  // Only fetch locally if no collections prop was provided
  useEffect(() => {
    if (collectionsProp !== undefined) return; // Parent is providing collections
    const loadCollections = async () => {
      if (!userId || !isPremium) return;
      
      try {
        const { data } = await supabase
          .from('custom_lists')
          .select('id, name')
          .eq('user_id', userId)
          .order('name');
        
        setLocalCollections(data || []);
      } catch (error) {
        console.error('Error loading collections:', error);
      }
    };

    loadCollections();
  }, [userId, isPremium, collectionsProp]);

  // Map certification to age
  const getCertificationWithAge = (cert: string | undefined) => {
    if (!cert) return null;
    const ageMap: Record<string, string> = {
      'G': 'G',
      'PG': 'PG (7+)',
      'PG-13': 'PG-13 (13+)',
      'R': 'R (17+)',
      'NC-17': '18+',
      'TV-Y': 'All',
      'TV-Y7': '7+',
      'TV-G': 'All',
      'TV-PG': 'PG',
      'TV-14': '14+',
      'TV-MA': '17+'
    };
    return ageMap[cert] || cert;
  };

  const addToCollection = async (collectionId: string, collectionName: string) => {
    if (!userId) {
      requestTicket("build collections");
      return;
    }

    try {
      const { error } = await supabase
        .from('list_items')
        .insert({
          list_id: collectionId,
          tmdb_id: id,
          title: title,
          poster_path: poster,
          media_type: media_type
        });

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Already in collection",
            description: `${title} is already in ${collectionName}`,
            variant: "destructive"
          });
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: "Added to collection",
        description: `${title} has been added to ${collectionName}`,
      });
    } catch (error) {
      console.error('Error adding to collection:', error);
      toast({
        title: "Error",
        description: "Failed to add to collection",
        variant: "destructive"
      });
    }
  };

  const formatRuntime = (minutes?: number) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const matchTone = ai_match_score != null && ai_match_score >= 80
    ? "screening-card__match--hot"
    : ai_match_score != null && ai_match_score >= 60
      ? "screening-card__match--warm"
      : "";

  return (
    <>
      <article
        data-tutorial="movie-card"
        className="screening-card group"
        onClick={() => setShowModal(true)}
        aria-label={`Open programme for ${title}`}
      >
        <div className="screening-card__bulbs" aria-hidden="true">
          {Array.from({ length: 18 }).map((_, i) => (
            <span key={i} style={{ animationDelay: `${i * 90}ms` }} />
          ))}
        </div>

        <div className="screening-card__main">
          <div className="screening-card__sprockets screening-card__sprockets--left" aria-hidden="true" />
          <div className="screening-card__sprockets screening-card__sprockets--right" aria-hidden="true" />

          <button
            type="button"
            className="screening-card__poster"
            onClick={() => setShowModal(true)}
            aria-label={`View details for ${title}`}
          >
            {!imageError && poster ? (
              <img
                src={poster}
                alt={title}
                loading="eager"
                className={cn("screening-card__image", imageLoaded && "screening-card__image--loaded")}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="screening-card__fallback">
                <Clapperboard className="h-8 w-8" />
                <span>{title}</span>
              </div>
            )}
            <span className="screening-card__vignette" aria-hidden="true" />
            <span className="screening-card__spotlight" aria-hidden="true" />
            <span className="screening-card__programme">
              <Info className="h-3.5 w-3.5" />
              Open programme
            </span>
          </button>

          {ratingLabel ? (
            rating > 0 && (
              <div className="screening-card__rating" aria-label={`${ratingLabel} ${rating} out of 10`} title={ratingLabel}>
                <Star className="h-3.5 w-3.5 fill-current" />
                <span>{Number.isInteger(rating) ? rating : rating.toFixed(1)}/10</span>
              </div>
            )
          ) : (
            <div className="screening-card__rating" aria-label={`Rating ${rating.toFixed(1)} out of 10`}>
              <Star className="h-3.5 w-3.5 fill-current" />
              <span>{rating.toFixed(1)}</span>
            </div>
          )}

          {certification && (
            <div className="screening-card__cert">{getCertificationWithAge(certification)}</div>
          )}

          {ai_match_score != null && ai_match_score > 0 && (
            <div className={cn("screening-card__match", matchTone)}>{ai_match_score}% match</div>
          )}

          <div className="screening-card__stub">
            <div className="screening-card__eyebrow">
              <Ticket className="h-3 w-3" />
              {media_type === 'tv' ? 'Series pass' : 'Feature ticket'}
            </div>
            <h3 className="screening-card__title">{title}</h3>
            <div className="screening-card__meta">
              {year && (
                <span><Calendar className="h-3 w-3" />{year}</span>
              )}
              {runtime && (
                <span><Clock className="h-3 w-3" />{formatRuntime(runtime)}</span>
              )}
              {!runtime && <span>{media_type === 'tv' ? 'TV' : 'Movie'}</span>}
            </div>

            {genres.length > 0 && (
              <div className="screening-card__genres">
                {genres.slice(0, 3).map((genre, index) => (
                  <span key={index}>{genre}</span>
                ))}
              </div>
            )}

            {genre_rating && genre_rating.score > 0 ? (
              <div className="screening-card__critic-note">
                <strong>{genre_rating.genre} {genre_rating.score}/10</strong>
                {genre_rating.note && <span>{genre_rating.note}</span>}
              </div>
            ) : (
              <p className="screening-card__overview">{overview || "No plot summary available."}</p>
            )}
          </div>

          <div className="screening-card__actions" onClick={(event) => event.stopPropagation()}>
            <WatchStatusSelector
              tmdbId={id}
              mediaType={media_type}
              userId={userId}
              title={title}
              posterPath={poster}
              releaseDate={year}
              currentStatus={currentWatchStatus}
              onStatusChange={onWatchStatusChange}
            />
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRatingDialog(true)}
              className="screening-card__icon-btn"
              aria-label={`Rate ${title}`}
            >
              <Star className="h-3.5 w-3.5" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowModal(true)}
              className="screening-card__icon-btn"
              aria-label={`Open programme for ${title}`}
            >
              <Info className="h-3.5 w-3.5" />
            </Button>

            {isPremium && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="screening-card__collection-btn"
                    aria-label={`Add ${title} to a collection`}
                  >
                    <FolderPlus className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">File</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Add to Collection
                  </div>
                  <DropdownMenuSeparator />
                  {collections.length === 0 ? (
                    <DropdownMenuItem disabled>
                      No collections yet — open Collections to create one
                    </DropdownMenuItem>
                  ) : (
                    collections.map((collection) => (
                      <DropdownMenuItem
                        key={collection.id}
                        onClick={() => addToCollection(collection.id, collection.name)}
                      >
                        <FolderPlus className="h-4 w-4 mr-2" />
                        {collection.name}
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </article>

      <MovieDetailModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        movieId={id}
        mediaType={media_type}
        userId={userId}
        isPremium={isPremium}
        pickReason={pickReason}
      />

      <RatingDialog
        isOpen={showRatingDialog}
        onClose={() => setShowRatingDialog(false)}
        movieId={id}
        movieTitle={title}
        mediaType={media_type}
        userId={userId}
      />
    </>
  );
});